import { useState, useEffect, createContext, useContext, useCallback, useRef } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";

// Pages
import LandingPage from "./pages/LandingPage";
import AuthPage from "./pages/AuthPage";
import DashboardLayout from "./pages/DashboardLayout";
import OpportunitiesPage from "./pages/OpportunitiesPage";
import OpportunityDetailPage from "./pages/OpportunityDetailPage";
import AIBuilderPage from "./pages/AIBuilderPage";
import MyProjectsPage from "./pages/MyProjectsPage";
import AnalyticsPage from "./pages/AnalyticsPage";
import SettingsPage from "./pages/SettingsPage";
import BillingPage from "./pages/BillingPage";
import DashboardIdeaCheckPage from "./pages/DashboardIdeaCheckPage";
import OnboardingPage from "./pages/OnboardingPage";
import AlertsPage from "./pages/AlertsPage";
import AdminPlanAccessPage from "./pages/AdminPlanAccessPage";
import PrivacyPolicyPage from "./pages/PrivacyPolicyPage";
import TermsOfServicePage from "./pages/TermsOfServicePage";
import CookiePolicyPage from "./pages/CookiePolicyPage";
import LegalNoticePage from "./pages/LegalNoticePage";

import { Toaster } from "./components/ui/sonner";
import LegalFooter from "./components/LegalFooter";
import { supabase } from "./lib/supabase";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const ALLOWED_TEST_PLANS = ["free", "pro", "full_access"];
const BACKEND_AUTH_TOKEN_KEY = "auth_token";
const BACKEND_AUTH_TOKEN_SOURCE_KEY = "auth_token_source";
const SUPABASE_ACCESS_TOKEN_KEY = "supabase_access_token";
const PLAN_OVERRIDE_ENABLED_KEY = "plan_override_enabled";
const IDLE_LAST_ACTIVITY_KEY = "idle_last_activity_ms";
const IDLE_TIMEOUT_HOURS = Number(process.env.REACT_APP_IDLE_TIMEOUT_HOURS || 24);
const IDLE_TIMEOUT_MS = Number.isFinite(IDLE_TIMEOUT_HOURS) && IDLE_TIMEOUT_HOURS > 0
  ? IDLE_TIMEOUT_HOURS * 60 * 60 * 1000
  : 24 * 60 * 60 * 1000;

const getPlanOverride = () => {
  const debugEnabled = localStorage.getItem(PLAN_OVERRIDE_ENABLED_KEY) === "true";
  const hasAdminPin = Boolean((localStorage.getItem("admin_plan_pin") || "").trim());
  if (!debugEnabled || !hasAdminPin) return null;
  const override = (localStorage.getItem("test_plan_override") || "").trim().toLowerCase();
  return ALLOWED_TEST_PLANS.includes(override) ? override : null;
};

const applyPlanOverrideToUser = (user) => {
  if (!user) return user;
  const override = getPlanOverride();
  if (!override) return user;
  return {
    ...user,
    plan: override,
  };
};

/** Match `_resolve_effective_plan` on the server: internal admins default to full_access. */
const mirrorServerPlanDefaults = (user) => {
  if (!user) return user;
  const internal = Boolean(user.is_admin) || String(user.role || "").toLowerCase() === "admin";
  if (!internal) return user;
  return { ...user, plan: "full_access" };
};

// Create axios instance with credentials
const apiClient = axios.create({
  baseURL: API,
  withCredentials: true
});

// Attach Authorization header from localStorage (fallback for cross-origin cookie issues)
apiClient.interceptors.request.use((config) => {
  // CSRF defense: a cross-site form submission can't set custom headers
  // (browsers refuse without a successful CORS preflight). Requiring this
  // header on mutating endpoints means a malicious site can't trigger a
  // cookie-authenticated POST/PUT/PATCH/DELETE on our API.
  config.headers["X-Requested-With"] = "XMLHttpRequest";

  const token = localStorage.getItem(BACKEND_AUTH_TOKEN_KEY);
  const tokenSource = localStorage.getItem(BACKEND_AUTH_TOKEN_SOURCE_KEY);
  if (token && (!tokenSource || tokenSource === "backend")) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  const testPlan = (localStorage.getItem("test_plan_override") || "").trim().toLowerCase();
  const debugEnabled = localStorage.getItem(PLAN_OVERRIDE_ENABLED_KEY) === "true";
  const testAdminKey = (localStorage.getItem("test_admin_key") || "").trim();
  const adminPlanPin = (localStorage.getItem("admin_plan_pin") || "").trim();
  if (debugEnabled && adminPlanPin && ["free", "pro", "full_access"].includes(testPlan)) {
    config.headers["X-Test-Plan"] = testPlan;
  }
  if (testAdminKey) {
    config.headers["X-Test-Admin-Key"] = testAdminKey;
  }
  if (adminPlanPin) {
    config.headers["X-Admin-Plan-Pin"] = adminPlanPin;
  }
  return config;
});

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export { API, apiClient };

// Auth Provider Component
const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const logoutRef = useRef(null);

  const mapSupabaseUser = useCallback((sessionUser) => {
    if (!sessionUser) return null;

    const identity = sessionUser.identities?.[0]?.identity_data || {};

    return {
      id: sessionUser.id,
      email: sessionUser.email,
      name:
        identity.full_name ||
        identity.name ||
        sessionUser.user_metadata?.full_name ||
        sessionUser.user_metadata?.name ||
        sessionUser.email ||
        "User",
      avatar_url:
        identity.avatar_url ||
        identity.picture ||
        sessionUser.user_metadata?.avatar_url ||
        sessionUser.user_metadata?.picture ||
        null,
      picture:
        identity.picture ||
        identity.avatar_url ||
        sessionUser.user_metadata?.picture ||
        sessionUser.user_metadata?.avatar_url ||
        null,
      user_metadata: sessionUser.user_metadata || {},
      identities: sessionUser.identities || [],
      plan: "free",
      auth_provider: sessionUser.app_metadata?.provider || "google",
    };
  }, []);

  const mergeBackendProfile = useCallback(
    async (supabaseUser) => {
      try {
        // Always try backend profile first; cookie-based auth may be valid
        // even when localStorage token is missing.
        const response = await apiClient.get("/auth/me");
        const merged = {
          ...response.data,
          avatar_url:
            supabaseUser?.identities?.[0]?.identity_data?.avatar_url ||
            supabaseUser?.identities?.[0]?.identity_data?.picture ||
            supabaseUser?.user_metadata?.avatar_url ||
            supabaseUser?.user_metadata?.picture ||
            response.data?.avatar_url ||
            response.data?.picture ||
            null,
          user_metadata: supabaseUser?.user_metadata || {},
          identities: supabaseUser?.identities || [],
          auth_provider: supabaseUser?.app_metadata?.provider || response.data?.auth_provider || "google",
        };
        return applyPlanOverrideToUser(mirrorServerPlanDefaults(merged));
      } catch (_error) {
        return applyPlanOverrideToUser(mapSupabaseUser(supabaseUser));
      }
    },
    [mapSupabaseUser]
  );

  const checkAuth = useCallback(async () => {
    try {
      const { data, error } = await supabase.auth.getSession();

      if (error) {
        console.error("Supabase session error:", error);
      }

      const accessToken = data?.session?.access_token || null;
      const sessionUser = data?.session?.user || null;

      let fullUser = sessionUser;
      if (sessionUser) {
        const { data: userData, error: userError } = await supabase.auth.getUser();
        if (userError) {
          console.error("Supabase getUser error:", userError);
        }
        fullUser = userData?.user || sessionUser;
        console.log("SUPABASE SESSION USER:", sessionUser);
        console.log("SUPABASE FULL USER:", fullUser);
      }

      if (accessToken) {
        localStorage.setItem(SUPABASE_ACCESS_TOKEN_KEY, accessToken);
      }

      if (fullUser) {
        setUser(await mergeBackendProfile(fullUser));
      } else {
        // Fallback for email/password auth backed by our API token
        const token = localStorage.getItem(BACKEND_AUTH_TOKEN_KEY);
        if (!token) {
          setUser(null);
        } else {
          try {
            const response = await apiClient.get("/auth/me");
            setUser(applyPlanOverrideToUser(mirrorServerPlanDefaults(response.data)));
          } catch (_apiError) {
            localStorage.removeItem(BACKEND_AUTH_TOKEN_KEY);
            localStorage.removeItem(BACKEND_AUTH_TOKEN_SOURCE_KEY);
            setUser(null);
          }
        }
      }
    } catch (error) {
      console.error("checkAuth error:", error);
      localStorage.removeItem(BACKEND_AUTH_TOKEN_KEY);
      localStorage.removeItem(BACKEND_AUTH_TOKEN_SOURCE_KEY);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, [mergeBackendProfile]);

  useEffect(() => {
    checkAuth();

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      const accessToken = session?.access_token || null;
      const sessionUser = session?.user || null;

      // Only mutate token storage for explicit Supabase auth events.
      if (event === "SIGNED_IN" && accessToken) {
        localStorage.setItem(SUPABASE_ACCESS_TOKEN_KEY, accessToken);
      }
      if (event === "SIGNED_OUT") {
        localStorage.removeItem(BACKEND_AUTH_TOKEN_KEY);
        localStorage.removeItem(BACKEND_AUTH_TOKEN_SOURCE_KEY);
        localStorage.removeItem(SUPABASE_ACCESS_TOKEN_KEY);
        setUser(null);
        setLoading(false);
        return;
      }

      const syncUser = async () => {
        let fullUser = sessionUser;
        if (sessionUser) {
          const { data: userData, error: userError } = await supabase.auth.getUser();
          if (userError) {
            console.error("Supabase getUser error (listener):", userError);
          }
          fullUser = userData?.user || sessionUser;
          console.log("SUPABASE LISTENER SESSION USER:", sessionUser);
          console.log("SUPABASE LISTENER FULL USER:", fullUser);
        }

        if (fullUser) {
          setUser(await mergeBackendProfile(fullUser));
        } else {
          const token = localStorage.getItem(BACKEND_AUTH_TOKEN_KEY);
          if (!token) {
            setUser(null);
          } else {
            try {
              const response = await apiClient.get("/auth/me");
              setUser(applyPlanOverrideToUser(mirrorServerPlanDefaults(response.data)));
            } catch (_apiError) {
              localStorage.removeItem(BACKEND_AUTH_TOKEN_KEY);
              localStorage.removeItem(BACKEND_AUTH_TOKEN_SOURCE_KEY);
              setUser(null);
            }
          }
        }
        setLoading(false);
      };

      syncUser();
    });

    return () => {
      subscription?.unsubscribe?.();
    };
  }, [checkAuth, mergeBackendProfile]);

  const login = (userData) => {
    setUser(applyPlanOverrideToUser(mirrorServerPlanDefaults(userData)));
    setLoading(false);
  };

  const logout = useCallback(async () => {
    try {
      await supabase.auth.signOut();
      await apiClient.post("/auth/logout");
    } catch (error) {
      console.error("Logout error:", error);
    }
    localStorage.removeItem(BACKEND_AUTH_TOKEN_KEY);
    localStorage.removeItem(BACKEND_AUTH_TOKEN_SOURCE_KEY);
    localStorage.removeItem(SUPABASE_ACCESS_TOKEN_KEY);
    setUser(null);
    setLoading(false);
  }, []);

  useEffect(() => {
    logoutRef.current = logout;
  }, [logout]);

  useEffect(() => {
    if (!user) return undefined;

    const markActivity = () => {
      const now = Date.now();
      localStorage.setItem(IDLE_LAST_ACTIVITY_KEY, String(now));
    };

    const maybeLogoutForInactivity = async () => {
      const lastRaw = localStorage.getItem(IDLE_LAST_ACTIVITY_KEY);
      const last = Number(lastRaw || 0);
      const baseline = Number.isFinite(last) && last > 0 ? last : Date.now();
      if (Date.now() - baseline < IDLE_TIMEOUT_MS) return;
      localStorage.removeItem(IDLE_LAST_ACTIVITY_KEY);
      await logoutRef.current?.();
    };

    markActivity();

    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        void maybeLogoutForInactivity();
      }
    };

    const events = ["click", "keydown", "mousemove", "scroll", "touchstart"];
    events.forEach((eventName) =>
      window.addEventListener(eventName, markActivity, { passive: true })
    );
    document.addEventListener("visibilitychange", onVisibilityChange);

    const timer = window.setInterval(() => {
      void maybeLogoutForInactivity();
    }, 60_000);

    return () => {
      window.clearInterval(timer);
      events.forEach((eventName) => window.removeEventListener(eventName, markActivity));
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [user]);

  return (
    <AuthContext.Provider value={{ user, setUser: login, logout, loading, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
};

// Auth Callback Component for Google OAuth
const AuthCallback = () => {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processSession = async () => {
      const hash = window.location.hash;
      const params = new URLSearchParams(hash.substring(1));
      const sessionId = params.get("session_id");

      if (!sessionId) {
        navigate("/auth");
        return;
      }

      try {
        const response = await apiClient.post("/auth/session", { session_id: sessionId });
        if (response.data.token) {
          localStorage.setItem(BACKEND_AUTH_TOKEN_KEY, response.data.token);
          localStorage.setItem(BACKEND_AUTH_TOKEN_SOURCE_KEY, "backend");
        }
        setUser(applyPlanOverrideToUser(mirrorServerPlanDefaults(response.data.user)));
        
        // Check if onboarding is needed
        if (!response.data.user.skill_level) {
          navigate("/onboarding", { replace: true });
        } else {
          navigate("/dashboard/opportunities", { replace: true });
        }
      } catch (error) {
        console.error("Session processing error:", error);
        navigate("/auth");
      }
    };

    processSession();
  }, [navigate, setUser]);

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
      <div className="text-center">
        <div className="w-12 h-12 border-4 border-[#7C3AED] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
        <p className="text-zinc-400">Authenticating...</p>
      </div>
    </div>
  );
};

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-[#7C3AED] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/auth" state={{ from: location }} replace />;
  }

  return children;
};

const AdminRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-[#7C3AED] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }
  if (!user) return <Navigate to="/auth" replace />;
  const isAdmin = Boolean(user?.is_admin) || String(user?.role || "").toLowerCase() === "admin";
  if (!isAdmin) return <Navigate to="/dashboard/settings" replace />;
  return children;
};

const AppRouter = () => {
  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<Navigate to="/auth" replace />} />
      <Route path="/auth" element={<AuthPage />} />
      <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
      <Route path="/terms-of-service" element={<TermsOfServicePage />} />
      <Route path="/cookie-policy" element={<CookiePolicyPage />} />
      <Route path="/legal-notice" element={<LegalNoticePage />} />
      <Route
        path="/internal/ops/plan"
        element={(
          <AdminRoute>
            <AdminPlanAccessPage />
          </AdminRoute>
        )}
      />
      <Route path="/onboarding" element={
        <ProtectedRoute>
          <OnboardingPage />
        </ProtectedRoute>
      } />
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <DashboardLayout />
        </ProtectedRoute>
      }>
        <Route index element={<Navigate to="opportunities" replace />} />
        <Route path="opportunities" element={<OpportunitiesPage />} />
        <Route path="opportunities/:id" element={<OpportunityDetailPage />} />
        <Route path="ai-builder" element={<AIBuilderPage />} />
        <Route path="ai-builder/:opportunityId" element={<AIBuilderPage />} />
        <Route path="idea-check" element={<DashboardIdeaCheckPage />} />
        <Route path="projects" element={<MyProjectsPage />} />
        <Route path="analytics" element={<AnalyticsPage />} />
        <Route path="alerts" element={<AlertsPage />} />
        <Route path="settings" element={<SettingsPage />} />
        <Route path="settings/billing" element={<BillingPage />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRouter />
        <LegalFooter />
        <Toaster position="top-right" theme="dark" />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
